(() => {
  if (customElements.get('hasbro-size-guide')) return;

  const triggerSelector = '.hasbro-size-guide-link[data-hasbro-size-guide-trigger]';
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  let activeGuide = null;
  let scrollLock = null;

  const isAvailable = (element) =>
    element instanceof HTMLElement &&
    !element.matches(':disabled, [hidden], [inert]') &&
    !element.closest('[hidden], [inert]') &&
    element.getClientRects().length > 0 &&
    getComputedStyle(element).visibility !== 'hidden';

  const focusWithoutScroll = (element) => {
    if (!isAvailable(element)) return false;
    element.focus({ preventScroll: true });
    return document.activeElement === element;
  };

  const lockPageScroll = (owner) => {
    if (scrollLock) return;
    const body = document.body;
    const html = document.documentElement;
    const x = window.scrollX;
    const y = window.scrollY;
    const alreadyFixed = getComputedStyle(body).position === 'fixed';
    const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth);
    const paddingRight = parseFloat(getComputedStyle(body).paddingRight) || 0;
    const properties = [];

    const apply = (element, property, value) => {
      const before = element.style.getPropertyValue(property);
      const priority = element.style.getPropertyPriority(property);
      element.style.setProperty(property, value, 'important');
      properties.push({ element, property, before, priority, applied: element.style.getPropertyValue(property) });
    };

    if (!alreadyFixed) {
      apply(body, 'position', 'fixed');
      apply(body, 'top', `${-y}px`);
      apply(body, 'left', `${-x}px`);
      apply(body, 'right', '0px');
      apply(body, 'width', '100%');
      apply(body, 'box-sizing', 'border-box');
      if (scrollbarWidth) apply(body, 'padding-right', `${paddingRight + scrollbarWidth}px`);
    }
    apply(body, 'overflow', 'hidden');
    apply(html, 'overflow', 'hidden');
    scrollLock = { owner, body, html, x, y, alreadyFixed, properties };
  };

  const unlockPageScroll = (owner) => {
    if (!scrollLock || scrollLock.owner !== owner) return;
    const { body, html, x, y, alreadyFixed, properties } = scrollLock;
    const positionRecord = properties.find(({ element, property }) => element === body && property === 'position');
    const ownsPosition = positionRecord && body.style.getPropertyValue('position') === positionRecord.applied;
    scrollLock = null;

    for (const { element, property, before, priority, applied } of properties.reverse()) {
      if (element.style.getPropertyValue(property) !== applied || element.style.getPropertyPriority(property) !== 'important') continue;
      if (before) element.style.setProperty(property, before, priority);
      else element.style.removeProperty(property);
    }

    if (!alreadyFixed && ownsPosition && getComputedStyle(body).position !== 'fixed') {
      const behavior = html.style.getPropertyValue('scroll-behavior');
      const priority = html.style.getPropertyPriority('scroll-behavior');
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(x, y);
      if (behavior) html.style.setProperty('scroll-behavior', behavior, priority);
      else html.style.removeProperty('scroll-behavior');
    }
  };

  class HasbroSizeGuide extends HTMLElement {
    #dialog;
    #scope;
    #controller;
    #opener;
    #openerContext;
    #backdropPointerDown = false;
    #restoreFocus = true;
    #opened = false;

    connectedCallback() {
      this.#connect();
    }

    #connect() {
      const dialog = this.querySelector('dialog');
      if (!dialog) return;
      const scope = this.closest('.shopify-section') || this.parentElement || document;
      if (dialog === this.#dialog && scope === this.#scope && this.#controller && !this.#controller.signal.aborted) return;
      if (this.#dialog && dialog !== this.#dialog) this.close({ restoreFocus: false });
      this.#controller?.abort();
      this.#dialog = dialog;
      this.#scope = scope;
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.#scope.addEventListener('click', this.#onTriggerClick, { signal });
      this.#dialog.addEventListener('click', this.#onDialogClick, { signal });
      this.#dialog.addEventListener('pointerdown', this.#onPointerDown, { signal });
      this.#dialog.addEventListener('cancel', this.#onCancel, { signal });
      this.#dialog.addEventListener('close', this.#onClosed, { signal });
      this.#dialog.addEventListener('keydown', this.#onKeyDown, { signal });
    }

    disconnectedCallback() {
      this.close({ restoreFocus: false });
      this.#controller?.abort();
      unlockPageScroll(this);
      if (activeGuide === this) activeGuide = null;
      this.#opener = null;
      this.#openerContext = null;
    }

    #ownsTrigger = (trigger) => {
      if (!this.#dialog || !this.#scope.contains(trigger)) return false;
      if (this.#scope.matches?.('.shopify-section') && trigger.closest('.shopify-section') !== this.#scope) return false;
      const productId = trigger.dataset.productId;
      if (productId && productId !== this.dataset.productId) return false;
      const controls = trigger.getAttribute('aria-controls')?.trim();
      if (controls) return controls.split(/\s+/).includes(this.#dialog.id);
      if (!productId) return false;
      const owner = [...this.#scope.querySelectorAll('hasbro-size-guide')].find(
        (guide) => guide.dataset.productId === productId && guide.closest('.shopify-section') === this.closest('.shopify-section'),
      );
      return owner === this;
    };

    #onTriggerClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest(triggerSelector);
      if (!trigger || !this.#ownsTrigger(trigger)) return;
      event.preventDefault();
      event.stopPropagation();
      this.open(trigger);
    };

    open(trigger) {
      this.#connect();
      if (!this.#dialog || this.#dialog.open || typeof this.#dialog.showModal !== 'function') return;
      if (activeGuide && activeGuide !== this) activeGuide.close({ restoreFocus: false });
      let hydrated = false;
      for (const template of this.#dialog.querySelectorAll('template[data-hasbro-size-guide-content]')) {
        if (template instanceof HTMLTemplateElement) {
          template.replaceWith(template.content.cloneNode(true));
          hydrated = true;
        }
      }
      if (hydrated || !this.#dialog.dataset.unit) this.#setUnit('in');
      this.#opener = trigger instanceof HTMLElement ? trigger : document.activeElement;
      this.#openerContext = this.#opener?.closest('[id^="ProductInformation"], product-component');
      this.#restoreFocus = true;
      this.#backdropPointerDown = false;
      lockPageScroll(this);
      try {
        this.#dialog.showModal();
      } catch (error) {
        unlockPageScroll(this);
        throw error;
      }
      this.#opened = true;
      activeGuide = this;
      this.#opener?.setAttribute('aria-expanded', 'true');
      this.#dialog.scrollTop = 0;
      const content = this.#dialog.querySelector('.hasbro-size-guide__body');
      if (content) content.scrollTop = 0;
      const heading = this.#dialog.querySelector('[data-hasbro-size-guide-heading], .hasbro-size-guide__title, h2, h1');
      if (heading) heading.setAttribute('tabindex', '-1');
      if (!focusWithoutScroll(heading)) {
        this.#dialog.setAttribute('tabindex', '-1');
        focusWithoutScroll(this.#dialog);
      }
    }

    close({ restoreFocus = true } = {}) {
      this.#restoreFocus = restoreFocus;
      if (this.#dialog?.open) this.#dialog.close();
      this.#finishClose();
    }

    #finishClose() {
      if (!this.#opened) return;
      this.#opened = false;
      unlockPageScroll(this);
      if (activeGuide === this) activeGuide = null;
      this.#opener?.setAttribute('aria-expanded', 'false');
      const replacements = [...(this.#scope?.querySelectorAll(triggerSelector) || [])].filter(this.#ownsTrigger);
      for (const trigger of replacements) trigger.setAttribute('aria-expanded', 'false');
      if (this.#restoreFocus && this.isConnected) {
        const replacement = replacements.find((trigger) => this.#openerContext?.contains(trigger) && isAvailable(trigger)) || replacements.find(isAvailable);
        if (!focusWithoutScroll(this.#opener)) focusWithoutScroll(replacement);
      }
      this.#opener = null;
      this.#openerContext = null;
      this.#backdropPointerDown = false;
    }

    #onClosed = () => {
      if (!this.#dialog.open) this.#finishClose();
    };

    #onCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    #outsideDialog = (event) => {
      const { left, right, top, bottom } = this.#dialog.getBoundingClientRect();
      return event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom;
    };

    #onPointerDown = (event) => {
      this.#backdropPointerDown = event.target === this.#dialog && this.#outsideDialog(event);
    };

    #setUnit(unit) {
      if (unit !== 'in' && unit !== 'cm') return false;
      const source = this.querySelector('script[data-hasbro-size-guide-data]');
      if (!source) return false;
      let guide;
      try {
        guide = JSON.parse(source.textContent);
      } catch {
        return false;
      }
      const rows = unit === 'cm' ? guide?.rows_cm : guide?.rows_in;
      const headers = guide?.headers;
      const table = this.#dialog.querySelector('.hsg-table, table');
      if (!table || !Array.isArray(rows) || !Array.isArray(headers)) return false;
      const tableRows = [...table.querySelectorAll('tbody tr')];
      const tableHeaders = [...table.querySelectorAll('thead th')];
      if (tableRows.length !== rows.length || tableHeaders.length !== headers.length) return false;
      if (tableRows.some((row, index) => !Array.isArray(rows[index]) || row.querySelectorAll('td').length !== rows[index].length)) return false;
      tableRows.forEach((row, rowIndex) => {
        row.querySelectorAll('td').forEach((cell, columnIndex) => {
          cell.textContent = rows[rowIndex][columnIndex];
        });
      });
      tableHeaders.forEach((header, index) => {
        const label = header.querySelector('[data-hsg-unit-label]') || header;
        label.textContent = String(headers[index]).replace('(in)', `(${unit})`);
      });
      for (const button of this.#dialog.querySelectorAll('[data-hsg-unit]')) {
        button.setAttribute('aria-pressed', String(button.dataset.hsgUnit === unit));
      }
      this.#dialog.dataset.unit = unit;
      return true;
    }

    #onDialogClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const unitButton = event.target.closest('[data-hsg-unit]');
      if (unitButton && this.#dialog.contains(unitButton)) {
        event.preventDefault();
        this.#setUnit(unitButton.dataset.hsgUnit);
        return;
      }
      const close = event.target.closest('[data-hasbro-size-guide-close]');
      if (close && this.#dialog.contains(close)) {
        event.preventDefault();
        this.close();
        return;
      }
      if (this.#backdropPointerDown && event.target === this.#dialog && this.#outsideDialog(event)) this.close();
      this.#backdropPointerDown = false;
    };

    #onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.#dialog.querySelectorAll(focusableSelector)].filter((element) => element.tabIndex >= 0 && isAvailable(element));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      if (!first) {
        event.preventDefault();
        focusWithoutScroll(this.#dialog.querySelector('[data-hasbro-size-guide-heading], .hasbro-size-guide__title'));
      } else if (!focusable.includes(current) || (event.shiftKey && current === first)) {
        event.preventDefault();
        focusWithoutScroll(event.shiftKey ? last : first);
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        focusWithoutScroll(first);
      }
    };
  }

  customElements.define('hasbro-size-guide', HasbroSizeGuide);

  // Resolve explicit ownership on activation, including sections parsed after registration.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(triggerSelector);
    const controls = trigger?.getAttribute('aria-controls')?.trim();
    const productId = trigger?.dataset.productId;
    const scope = trigger?.closest('.shopify-section');
    if (!controls || /\s/.test(controls) || !productId || !scope) return;
    const dialog = document.getElementById(controls);
    const guide = dialog?.closest('hasbro-size-guide');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.isConnected || !(guide instanceof HasbroSizeGuide)) return;
    if (guide.dataset.productId !== productId || guide.closest('.shopify-section') !== scope) return;
    event.preventDefault();
    event.stopPropagation();
    guide.open(trigger);
  }, { capture: true });
})();
